import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  useMotionValueEvent,
} from "motion/react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  Environment,
  ContactShadows,
  Float,
} from "@react-three/drei";
import { Box, Layers, Cpu, Hand, ArrowRight, MoveHorizontal } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import MainCategories from "./MainCategories";

/* ─── 3-D SHAPES ─────────────────────────────── */
function AbstractShape() {
  return (
    <Float speed={2} rotationIntensity={0.5} floatIntensity={1}>
      <mesh>
        <torusKnotGeometry args={[1, 0.3, 128, 32]} />
        <meshPhysicalMaterial
          color="#14b8a6"
          roughness={0.1}
          metalness={0.8}
          clearcoat={1}
          clearcoatRoughness={0.1}
        />
      </mesh>
    </Float>
  );
}

function ARVRShape() {
  return (
    <Float speed={2} rotationIntensity={0.5} floatIntensity={1}>
      <group>
        <mesh>
          <icosahedronGeometry args={[1.2, 1]} />
          <meshStandardMaterial
            color="#3b82f6"
            wireframe
            transparent
            opacity={0.3}
          />
        </mesh>
        <mesh>
          <icosahedronGeometry args={[0.8, 1]} />
          <meshStandardMaterial
            color="#3b82f6"
            metalness={0.8}
            roughness={0.2}
          />
        </mesh>
      </group>
    </Float>
  );
}

function HardwareShape() {
  return (
    <Float speed={2} rotationIntensity={1} floatIntensity={2}>
      <group>
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[2, 0.2, 2]} />
          <meshStandardMaterial
            color="#ea580c"
            metalness={0.5}
            roughness={0.5}
          />
        </mesh>
        <mesh position={[0, 0.15, 0]}>
          <boxGeometry args={[1, 0.1, 1]} />
          <meshStandardMaterial
            color="#1f2937"
            metalness={0.9}
            roughness={0.1}
          />
        </mesh>
        {Array.from({ length: 8 }).map((_, i) => (
          <mesh key={`pin-top-${i}`} position={[-0.875 + i * 0.25, 0, -1.1]}>
            <boxGeometry args={[0.1, 0.1, 0.4]} />
            <meshStandardMaterial
              color="#eab308"
              metalness={1}
              roughness={0.2}
            />
          </mesh>
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <mesh key={`pin-bottom-${i}`} position={[-0.875 + i * 0.25, 0, 1.1]}>
            <boxGeometry args={[0.1, 0.1, 0.4]} />
            <meshStandardMaterial
              color="#eab308"
              metalness={1}
              roughness={0.2}
            />
          </mesh>
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <mesh key={`pin-left-${i}`} position={[-1.1, 0, -0.875 + i * 0.25]}>
            <boxGeometry args={[0.4, 0.1, 0.1]} />
            <meshStandardMaterial
              color="#eab308"
              metalness={1}
              roughness={0.2}
            />
          </mesh>
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <mesh key={`pin-right-${i}`} position={[1.1, 0, -0.875 + i * 0.25]}>
            <boxGeometry args={[0.4, 0.1, 0.1]} />
            <meshStandardMaterial
              color="#eab308"
              metalness={1}
              roughness={0.2}
            />
          </mesh>
        ))}
      </group>
    </Float>
  );
}

/* ─── INTERACTIVE VIEWER ─────────────────────── */
function InteractiveViewer({ children }: { children: React.ReactNode }) {
  const [isInteracting, setIsInteracting] = useState(false);

  return (
    <div className="w-full h-full relative group select-none">
      <Canvas
        camera={{ position: [0, 0, 5], fov: 45 }}
        className="pointer-events-none md:!pointer-events-auto"
      >
        <ambientLight intensity={0.6} />
        <spotLight
          position={[10, 10, 10]}
          angle={0.15}
          penumbra={1}
          intensity={1.2}
        />
        {children}
        <Environment preset="city" />
        <ContactShadows
          position={[0, -1.5, 0]}
          opacity={0.4}
          scale={10}
          blur={2}
          far={4}
        />
        <OrbitControls
          makeDefault
          enableZoom={false}
          enablePan={false}
          autoRotate={!isInteracting}
          autoRotateSpeed={2.5}
          onStart={() => setIsInteracting(true)}
          onEnd={() => setIsInteracting(false)}
        />
      </Canvas>

      {/* Desktop Hint */}
      <div
        className="hidden md:flex absolute bottom-4 left-1/2 -translate-x-1/2 items-center gap-2 text-xs uppercase tracking-widest text-black/50 dark:text-white/50 pointer-events-none font-Montserrat"
      >
        <motion.div
          animate={{ x: [-4, 4, -4] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        >
          <Hand size={14} />
        </motion.div>
        Drag to rotate
      </div>
    </div>
  );
}

/* ─── DESKTOP STEP CARD (Original Desktop Design) ─── */
function DesktopStep({
  index,
  title,
  description,
  icon: Icon,
  colorClass,
  cardClass,
  iconClass,
  link,
  children,
}: {
  index: number;
  title: string;
  description: string;
  icon: React.ComponentType<{ size?: number }>;
  colorClass: string;
  cardClass?: string;
  iconClass?: string;
  link: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <motion.div
      key={`step-card-${index}`}
      initial={{ opacity: 0, y: 70, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -70, scale: 0.96 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="kivas-tech-step absolute inset-0 flex items-center justify-center p-6 md:p-12 z-40 pointer-events-auto"
    >
      <div
        onClick={() => navigate(link || "/work")}
        className={`kivas-tech-step-card w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
          cardClass || "bg-white/10 dark:bg-[#050505]/10 border-black/10 dark:border-white/10"
        } backdrop-blur-xl rounded-4xl border p-8 md:p-16 shadow-2xl relative overflow-hidden cursor-pointer group/card transition-all duration-300 hover:scale-[1.008]`}
      >
        {/* Glass reflection */}
        <div className="absolute inset-0 bg-linear-to-br from-white/40 via-white/5 to-transparent dark:from-white/10 dark:via-white/0 dark:to-transparent pointer-events-none" />

        <div className="flex flex-col gap-6 relative z-10">
          {/* Icon box */}
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center border ${
              iconClass || "bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10 text-black dark:text-white"
            }`}
          >
            <Icon size={32} />
          </div>

          {/* Step label */}
          <div
            className={`text-sm tracking-widest uppercase ${colorClass} opacity-80 font-Montserrat`}
          >
            0{index + 1}
          </div>

          {/* Title */}
          <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-black dark:text-white font-Montserrat">
            {title}
          </h3>

          {/* Description */}
          <p className="text-lg text-black/70 dark:text-white/70 leading-relaxed mb-4 text-justify font-Montserrat">
            {description}
          </p>

          {/* Explore button */}
          <Link
            to={link || "/work"}
            onClick={(e) => e.stopPropagation()}
            className={`group/btn inline-flex items-center gap-3 px-8 py-3.5 rounded-full text-sm md:text-base font-semibold tracking-wider uppercase border border-current bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 active:scale-95 transition-all duration-300 shadow-lg w-fit ${colorClass} font-Montserrat`}
          >
            <span>Explore</span>
            <ArrowRight
              size={20}
              className="group-hover/btn:translate-x-1.5 transition-transform duration-300"
            />
          </Link>
        </div>

        {/* 3-D viewer */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="kivas-tech-viewer h-75 lg:h-125 w-full rounded-2xl overflow-hidden bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/5 relative backdrop-blur-md z-10 shadow-inner cursor-default"
        >
          {children}
        </div>
      </div>
    </motion.div>
  );
}

/* ─── DESKTOP PROGRESS DOT ───────────────────── */
function ProgressDot({
  isActive,
  label,
  colorClass,
}: {
  isActive: boolean;
  label: string;
  colorClass: string;
}) {
  return (
    <div className="relative flex items-center group">
      <motion.div
        animate={{ scale: isActive ? 1.5 : 1, opacity: isActive ? 1 : 0.3 }}
        className={`w-3 h-3 rounded-full border-2 border-current ${colorClass} bg-current`}
      />
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: isActive ? 1 : 0, x: isActive ? 0 : -10 }}
        className="absolute left-8 text-[10px] uppercase tracking-widest whitespace-nowrap text-black dark:text-white font-Montserrat"
      >
        {label}
      </motion.div>
    </div>
  );
}

/* ─── ROOT TECHNOLOGY COMPONENT ───────────────────────────── */
export default function TechPillars() {
  const containerRef = useRef(null);
  const [activePillar, setActivePillar] = useState(-1);
  const [mobileSelectedPillar, setMobileSelectedPillar] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const navigate = useNavigate();

  const total = 3;

  const next = useCallback(() => {
    setDirection("right");
    setMobileSelectedPillar((prev) => (prev + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    setDirection("left");
    setMobileSelectedPillar((prev) => (prev - 1 + total) % total);
  }, [total]);

  const goTo = useCallback(
    (index: number) => {
      const activeNormalized = mobileSelectedPillar % total;
      if (index === activeNormalized) return;
      setDirection(index > activeNormalized ? "right" : "left");
      setMobileSelectedPillar(index);
    },
    [mobileSelectedPillar, total]
  );

  // Trackpad horizontal 2-finger scroll listener (matching Blog section)
  const wheelLockRef = useRef(false);
  const wheelAccumulatorRef = useRef(0);
  const wheelTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const sectionEl = document.getElementById("technology-mobile");
    if (!sectionEl) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > 0 && Math.abs(e.deltaX) >= Math.abs(e.deltaY)) {
        e.preventDefault();
        if (wheelLockRef.current) return;

        wheelAccumulatorRef.current += e.deltaX;

        if (Math.abs(wheelAccumulatorRef.current) > 35) {
          if (wheelAccumulatorRef.current > 0) {
            next();
          } else {
            prev();
          }
          wheelLockRef.current = true;
          wheelAccumulatorRef.current = 0;

          if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
          wheelTimerRef.current = setTimeout(() => {
            wheelLockRef.current = false;
            wheelAccumulatorRef.current = 0;
          }, 650);
        }
      } else {
        wheelAccumulatorRef.current = 0;
      }
    };

    sectionEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      sectionEl.removeEventListener("wheel", handleWheel);
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
    };
  }, [next, prev]);

  // Touch handlers for mobile finger swipes (matching Blog section)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const isSwipingRef = useRef(false);
  const touchLockRef = useRef(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    isSwipingRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const deltaX = touchStartRef.current.x - e.touches[0].clientX;
    const deltaY = touchStartRef.current.y - e.touches[0].clientY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      isSwipingRef.current = true;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current || touchLockRef.current) {
      touchStartRef.current = null;
      return;
    }
    const deltaX = touchStartRef.current.x - e.changedTouches[0].clientX;
    const deltaY = touchStartRef.current.y - e.changedTouches[0].clientY;

    if (Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY)) {
      touchLockRef.current = true;
      if (deltaX > 0) {
        next();
      } else {
        prev();
      }
      setTimeout(() => {
        touchLockRef.current = false;
      }, 450);
    }
    touchStartRef.current = null;
  };

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (latest < 0.12) {
      setActivePillar(-1);
    } else if (latest < 0.44) {
      setActivePillar(0);
    } else if (latest < 0.74) {
      setActivePillar(1);
    } else {
      setActivePillar(2);
    }
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 0.9]);
  const heroBlur = useTransform(
    scrollYProgress,
    [0, 0.15],
    ["blur(0px)", "blur(10px)"],
  );
  const bgRotate = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.15, 1],
    [0.3, 0.05, 0.05],
  );
  const thumbY = useTransform(scrollYProgress, [0.15, 1], ["0%", "100%"]);
  const progressOpacity = useTransform(scrollYProgress, [0.1, 0.15], [0, 1]);

  const pillars = [
    {
      id: "animation",
      tabLabel: "Animation Studio",
      number: "01",
      title: "Animation Studio",
      tagline: "High-Impact 3D Visuals & Optical Illusions",
      description:
        "KIVAS transforms ideas into stunning visual experiences producing high-impact 3D content for screens of every scale, from social feeds to stadium facades. Whether it's an optical illusion on a city billboard, motion graphics for a product launch, or synchronized visuals for a live concert—every piece is crafted to command attention, spark conversation, and leave a lasting impression.",
      icon: Box,
      accentRgb: "20, 184, 166",
      colorClass: "text-teal-600 dark:text-teal-400",
      cardClass:
        "bg-teal-400/10 dark:bg-teal-500/10 border-white/30 dark:border-white/10 shadow-[0_8px_32px_0_rgba(20,184,166,0.15)]",
      iconClass:
        "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20",
      badgeColor: "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20",
      cardBorder: "border-teal-500/30",
      link: "/services/3d-animation",
      shape: <AbstractShape />,
    },
    {
      id: "technology",
      tabLabel: "Technology",
      number: "02",
      title: "Technology & Software",
      tagline: "Intelligent Systems, Web & Spatial Platforms",
      description:
        "KIVAS excels in engineering intelligent digital solutions tailored to the unique demands of every industry building immersive digital experiences, powerful software, seamless applications and tools that simplify operations. From custom enterprise platforms to AI models that process live data as it happens, every system KIVAS builds is designed to perform in real time, in real space, in front of real audiences across web, mobile, and immersive environments.",
      icon: Layers,
      accentRgb: "59, 130, 246",
      colorClass: "text-blue-600 dark:text-blue-400",
      cardClass:
        "bg-blue-400/10 dark:bg-blue-500/10 border-white/30 dark:border-white/10 shadow-[0_8px_32px_0_rgba(59,130,246,0.15)]",
      iconClass:
        "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
      badgeColor: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
      cardBorder: "border-blue-500/30",
      link: "/services/ar-vr",
      shape: <ARVRShape />,
    },
    {
      id: "hardware",
      tabLabel: "Hardware",
      number: "03",
      title: "Hardware Solutions",
      tagline: "LED Screens, Holographic Tubes & Kiosks",
      description:
        "Every extraordinary experience demands an extraordinary foundation. KIVAS deploys and integrates premium display and interactive hardware the physical infrastructure that brings every visual, digital, and immersive experience to life. Available for both rental and purchase with turn-key setup and engineering support.",
      icon: Cpu,
      accentRgb: "234, 88, 12",
      colorClass: "text-orange-600 dark:text-orange-400",
      cardClass:
        "bg-orange-400/10 dark:bg-orange-500/10 border-white/30 dark:border-white/10 shadow-[0_8px_32px_0_rgba(234,88,12,0.15)]",
      iconClass:
        "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20",
      badgeColor: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20",
      cardBorder: "border-orange-500/30",
      link: "/services/hardware",
      shape: <HardwareShape />,
    },
  ];

  const currentMobile = pillars[mobileSelectedPillar];

  return (
    <>
      {/* ── 1. MOBILE VIEW (< md): Idea 1 Segmented Switcher + Glass Card ── */}
      <section
        id="technology-mobile"
        className="block md:hidden relative bg-transparent pt-28 pb-12 z-10 overflow-hidden"
      >
        <div className="w-full max-w-[1280px] mx-auto px-4">
          {/* Eyebrow */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-4 flex items-center gap-4"
          >
            <div className="w-4 h-px bg-indigo-500/50" />
            <span>Our Technology</span>
          </motion.div>

          {/* Hero Heading */}
          <div className="mb-8">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-Montserrat text-4xl font-bold tracking-tight text-black dark:text-white leading-[1.1]"
            >
              Three Domains.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-black dark:from-white via-indigo-600 dark:via-indigo-200 to-indigo-500/50 italic font-serif">
                One Vision.
              </span>
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-Montserrat text-sm text-black/60 dark:text-white/60 mt-3 leading-relaxed"
            >
              We build at the intersection of digital and physical reality,
              leveraging cutting-edge 3D, spatial computing, and hardware engineering.
            </motion.p>
          </div>

          {/* Segmented Pill Switcher */}
          <div className="mb-6">
            <div className="flex items-center gap-1.5 p-1.5 bg-black/[0.04] dark:bg-white/[0.04] rounded-2xl border border-black/5 dark:border-white/10 w-full overflow-x-auto no-scrollbar">
              {pillars.map((pillar, idx) => {
                const isSelected = mobileSelectedPillar === idx;
                const Icon = pillar.icon;
                return (
                  <button
                    key={`mobile-tab-${pillar.id}`}
                    onClick={() => goTo(idx)}
                    type="button"
                    className={`relative flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl font-Montserrat text-xs font-semibold transition-all duration-300 shrink-0 cursor-pointer ${
                      isSelected
                        ? "text-black dark:text-white shadow-sm"
                        : "text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white"
                    }`}
                  >
                    {isSelected && (
                      <motion.div
                        layoutId="activePillarPillMobile"
                        className="absolute inset-0 bg-white dark:bg-[#121212] rounded-xl border border-black/10 dark:border-white/15 shadow-sm"
                        transition={{ type: "spring", stiffness: 350, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-1.5">
                      <Icon size={14} className={isSelected ? pillar.colorClass : "opacity-60"} />
                      <span>{pillar.tabLabel}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Swipeable Stage with identical sliding animation deck to Blog section */}
          <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="w-full touch-pan-y"
          >
            <div className="relative w-full overflow-hidden min-h-[640px] sm:min-h-[610px] h-[640px] sm:h-[610px]">
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={currentMobile.id}
                  custom={direction}
                  variants={{
                    enter: (dir: "left" | "right") => ({
                      x: dir === "right" ? "105%" : "-105%",
                      opacity: 0.4,
                      scale: 0.94,
                    }),
                    center: {
                      x: 0,
                      opacity: 1,
                      scale: 1,
                    },
                    exit: (dir: "left" | "right") => ({
                      x: dir === "right" ? "-105%" : "105%",
                      opacity: 0.4,
                      scale: 0.94,
                    }),
                  }}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.3 },
                    scale: { duration: 0.35 },
                  }}
                  className="w-full h-full absolute inset-0"
                >
                  <div
                    className={`relative w-full h-full rounded-3xl border ${currentMobile.cardBorder} bg-white/60 dark:bg-[#0a0a0a]/60 backdrop-blur-xl p-5 sm:p-6 overflow-hidden shadow-xl flex flex-col justify-between`}
                  >
                    {/* Ambient Glow */}
                    <div
                      className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none opacity-35 blur-[70px]"
                      style={{ background: `rgba(${currentMobile.accentRgb}, 0.35)` }}
                    />

                    <div className="flex flex-col gap-3.5 relative z-10">
                      {/* Title */}
                      <h2 className="font-Montserrat text-2xl sm:text-3xl font-bold tracking-tight text-black dark:text-white">
                        {currentMobile.title}
                      </h2>

                      {/* 3D Interactive Viewer Box on Mobile */}
                      <div className="h-52 sm:h-56 w-full rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] border border-black/5 dark:border-white/10 overflow-hidden relative shadow-inner">
                        <InteractiveViewer>{currentMobile.shape}</InteractiveViewer>
                      </div>

                      {/* Description */}
                      <p className="font-Montserrat text-sm sm:text-base text-black/80 dark:text-white/80 leading-relaxed text-justify">
                        {currentMobile.description}
                      </p>
                    </div>

                    {/* CTA Button pinned to bottom */}
                    <div className="pt-2 mt-auto relative z-10">
                      <button
                        onClick={() => {
                          if (!isSwipingRef.current) navigate(currentMobile.link);
                        }}
                        type="button"
                        className="interactive inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full font-Montserrat text-sm font-semibold uppercase tracking-wider bg-black dark:bg-white text-white dark:text-black hover:opacity-90 active:scale-[0.97] transition-all duration-300 shadow-lg shadow-black/10 cursor-pointer w-full"
                      >
                        <span>Explore {currentMobile.tabLabel}</span>
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Interactive Pagination Indicator Dots (identical to BlogPage) */}
          <div className="flex items-center justify-center gap-2.5 mt-6">
            {pillars.map((_, i) => {
              const isActive = mobileSelectedPillar === i;
              return (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  type="button"
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    isActive
                      ? "w-8 h-2 bg-indigo-600 dark:bg-indigo-400 shadow-md scale-105"
                      : "w-2 h-2 bg-black/20 dark:bg-white/20 hover:bg-black/40 dark:hover:bg-white/40"
                  }`}
                  aria-label={`Go to ${pillars[i].tabLabel}`}
                />
              );
            })}
          </div>

          {/* Mobile Prompt: Swipe Left / Right Indicator (identical to BlogPage) */}
          <div className="flex items-center justify-center gap-2 text-xs font-Montserrat text-black/50 dark:text-white/50 mt-4 tracking-wider uppercase">
            <MoveHorizontal className="w-3.5 h-3.5 text-indigo-500 animate-pulse" />
            <span>Swipe left or right to explore</span>
          </div>
        </div>
      </section>

      {/* ── 2. DESKTOP VIEW (>= md): Exact Original 3D Sticky Scroll Sequence ── */}
      <section
        ref={containerRef}
        id="technology"
        className="hidden md:block relative h-[400svh] bg-transparent"
      >
        <div className="kivas-tech-sticky sticky top-0 h-[100svh] w-full overflow-hidden flex items-center justify-center pt-32">
          {/* Rotating SVG bg */}
          <motion.div
            style={{ rotate: bgRotate, opacity: bgOpacity }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <svg
              viewBox="0 0 1000 1000"
              className="w-[150vw] h-[150vw] max-w-none text-black dark:text-white"
            >
              <circle
                cx="500"
                cy="500"
                r="200"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="10 20"
              />
              <circle
                cx="500"
                cy="500"
                r="300"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
              <circle
                cx="500"
                cy="500"
                r="400"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="10 30"
              />
              <circle
                cx="500"
                cy="500"
                r="450"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </motion.div>

          {/* Blobs */}
          <motion.div
            style={{ opacity: progressOpacity }}
            className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center"
          >
            <div className="absolute top-[10%] left-[10%] w-[30vw] h-[30vw] bg-teal-400/20 dark:bg-teal-500/20 rounded-full blur-[100px]" />
            <div className="absolute bottom-[10%] right-[10%] w-[30vw] h-[30vw] bg-blue-400/20 dark:bg-blue-500/20 rounded-full blur-[100px]" />
            <div className="absolute top-[40%] left-[40%] w-[30vw] h-[30vw] bg-orange-400/20 dark:bg-orange-500/20 rounded-full blur-[100px]" />
          </motion.div>

          {/* Hero text */}
          <motion.div
            style={{ opacity: heroOpacity, scale: heroScale, filter: heroBlur }}
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-10 pointer-events-none"
          >
            {/* Eyebrow */}
            <div className="text-sm uppercase tracking-widest text-blue-500 mb-6 font-Montserrat">
              Our Technology
            </div>

            {/* Hero headline */}
            <h1 className="text-6xl md:text-8xl lg:text-[120px] font-bold tracking-tight leading-none text-black dark:text-white font-Montserrat">
              Three Domains.
              <br />
              <span className="italic">One Vision.</span>
            </h1>

            {/* Sub-copy */}
            <p className="mt-8 text-lg md:text-xl text-black/60 dark:text-white/60 max-w-2xl text-justify font-Montserrat">
              We build at the intersection of digital and physical reality,
              leveraging cutting-edge 3D, spatial computing, and hardware
              engineering.
            </p>
          </motion.div>

          {/* Left rail progress */}
          <motion.div
            style={{ opacity: progressOpacity }}
            className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 h-[60vh] w-1 bg-black/5 dark:bg-white/5 rounded-full hidden md:flex flex-col justify-between items-center py-4 z-50"
          >
            <motion.div
              style={{ top: thumbY }}
              className="absolute left-1/2 -translate-x-1/2 w-1.5 h-16 bg-black dark:bg-white rounded-full shadow-[0_0_10px_rgba(0,0,0,0.5)] dark:shadow-[0_0_10px_rgba(255,255,255,0.5)]"
            />
            {pillars.map((p, i) => (
              <ProgressDot
                key={p.title}
                isActive={activePillar === i}
                label={p.title}
                colorClass={p.colorClass}
              />
            ))}
          </motion.div>

          {/* Active Pillar Card with strict mode="wait" - ZERO OVERLAP */}
          <AnimatePresence mode="wait">
            {activePillar >= 0 && activePillar < pillars.length && (
              <DesktopStep
                key={pillars[activePillar].title}
                index={activePillar}
                title={pillars[activePillar].title}
                description={pillars[activePillar].description}
                icon={pillars[activePillar].icon}
                colorClass={pillars[activePillar].colorClass}
                cardClass={pillars[activePillar].cardClass}
                iconClass={pillars[activePillar].iconClass}
                link={pillars[activePillar].link}
              >
                <InteractiveViewer>
                  {pillars[activePillar].shape}
                </InteractiveViewer>
              </DesktopStep>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── 3. Capabilities / Bento Grid ── */}
      <MainCategories />
    </>
  );
}
